import React, { useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery } from "react-query";
import PatientService from "../services/PatientService";
import { HealthMetric } from "../types";
import {
  OwcTypography,
  OwcButton,
  OwcCard,
  OwcTable,
  OwcTableBody,
  OwcTableCell,
  OwcTableHeader,
  OwcTableHeaderCell,
  OwcTableRow,
  OwcToggleButton,
  OwcToggleButtonGroup,
  OwcProgressSpinner,
  OwcHeaderRow,
  OwcIconButton,
  OwcIcon,
  OwcMenu,
  OwcListItem,
} from "@one/react";
import ReactApexChart from "react-apexcharts";
import { ApexOptions } from "apexcharts";

// CSS styles for patient banner and charts
import "./PatientDetail.css";
import "./PatientChart.css";

// NoDataDisplay component for consistent empty state UI
interface NoDataDisplayProps {
  metricName: string; // e.g., "blood glucose", "weight", etc.
}

const NoDataDisplay: React.FC<NoDataDisplayProps> = ({ metricName }) => {
  return (
    <div className="no-data-container">
      <OwcIcon name="search" family="outlined" className="icon" />
      <div className="upper-text">No data available</div>
      <div className="lower-text">
        No {metricName} readings for the selected time period
      </div>
    </div>
  );
};

const PatientDetail: React.FC = () => {
  const { patientId } = useParams<{ patientId: string }>();
  const navigate = useNavigate();
  const headerRowRef = useRef<HTMLOwcHeaderRowElement>(null);

  // States
  // Using array for displayMode to match OwcToggleButtonGroup expected type
  const [displayMode, setDisplayMode] = useState<string[]>(["graph"]);
  const [daysToShow, setDaysToShow] = useState<number>(7);

  // Fetch patient details
  const {
    data: patientDetails,
    isLoading: isLoadingPatient,
    error: patientError,
  } = useQuery(
    ["patient", patientId],
    () => (patientId ? PatientService.getPatientDetails(patientId) : null),
    { enabled: !!patientId }
  );

  // Fetch blood glucose data
  const { data: bloodGlucoseReadings, isLoading: isLoadingBloodGlucose } =
    useQuery(
      ["patientBloodGlucose", patientId, daysToShow],
      () =>
        patientId
          ? PatientService.getPatientBloodGlucose(patientId, daysToShow)
          : [],
      { enabled: !!patientId }
    );

  // Fetch weight data
  const { data: weightReadings, isLoading: isLoadingWeight } = useQuery(
    ["patientWeight", patientId, daysToShow],
    () =>
      patientId ? PatientService.getPatientWeight(patientId, daysToShow) : [],
    { enabled: !!patientId }
  );

  // Fetch calories data
  const { data: caloriesReadings, isLoading: isLoadingCalories } = useQuery(
    ["patientCalories", patientId, daysToShow],
    () =>
      patientId ? PatientService.getPatientCalories(patientId, daysToShow) : [],
    { enabled: !!patientId }
  );

  // Fetch activity data
  const { data: activityReadings, isLoading: isLoadingActivity } = useQuery(
    ["patientActivity", patientId, daysToShow],
    () =>
      patientId ? PatientService.getPatientActivity(patientId, daysToShow) : [],
    { enabled: !!patientId }
  );

  // Helper functions
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString();
  };

  const handleDaysChange = (days: number) => {
    setDaysToShow(days);
  };

  // Combined loading state
  const isLoading =
    isLoadingPatient ||
    isLoadingBloodGlucose ||
    isLoadingWeight ||
    isLoadingCalories ||
    isLoadingActivity;
  const error = patientError;

  if (isLoading) {
    return (
      <div
        className="owc-flex owc-justify-center owc-align-center"
        style={{ minHeight: "400px" }}
      >
        <OwcProgressSpinner></OwcProgressSpinner>
      </div>
    );
  }

  if (error || !patientDetails) {
    return (
      <div className="owc-p-3">
        <OwcTypography style={{ color: "var(--owc-color-critical)" }}>
          Error loading patient details. Please try again later.
        </OwcTypography>
        <OwcButton
          variant="primary"
          size="medium"
          onClick={() => navigate("/patients")}
          style={{ marginTop: "16px" }}
        >
          Back to Patient List
        </OwcButton>
      </div>
    );
  }

  // Define types for our metrics data
  interface BloodGlucoseReading extends HealthMetric {}
  interface WeightReading extends HealthMetric {}
  interface CalorieReading {
    timestamp: string;
    value: number;
    unit?: string;
  }
  interface ActivityReading {
    timestamp: string;
    minutes: number;
  }

  // Process data for display - all data is already filtered by days in the API call
  const bloodGlucoseData: BloodGlucoseReading[] = bloodGlucoseReadings || [];
  const weightData: WeightReading[] = weightReadings || [];
  const caloriesData: CalorieReading[] = caloriesReadings || [];
  const activityData: ActivityReading[] = activityReadings || [];

  // Log activity data when it changes
  console.log("Activity Data:", activityData);
  if (activityData.length > 0) {
    console.log(
      "Minutes values:",
      activityData.map((item) => item.minutes)
    );
    console.log(
      "Min minutes:",
      Math.min(...activityData.map((item) => item.minutes))
    );
    console.log(
      "Max minutes:",
      Math.max(...activityData.map((item) => item.minutes))
    );
  }

  // ApexCharts configuration helpers
  const formatChartDate = (timestamp: string | number): string => {
    return new Date(timestamp).toLocaleDateString();
  };

  // Helper to format units for different metrics
  const getUnitDisplay = (
    unit: string,
    isShortForm: boolean = false
  ): string => {
    switch (unit.toLowerCase()) {
      case "minutes":
        return isShortForm ? "min" : "minutes";
      case "kcal":
        return isShortForm ? "kcal" : "kcal";
      case "mg/dl":
      case "mg/dL":
        return isShortForm ? "mg/dL" : "mg/dL";
      default:
        return unit;
    }
  };

  // Formats the value without unit for y-axis labels
  const formatYAxisValue = (value: number): string => {
    return value.toString();
  };

  // LINE_CHART_OPTIONS integrated with our ApexChart configuration
  const getApexChartOptions = (
    title: string,
    unit: string,
    color: string,
    data: any[]
  ): ApexOptions => {
    // Find min and max values for better y-axis scaling
    let min = 0;
    let max = 0;
    let valueKey = "value";

    // Determine which property to use based on the metric type
    if (unit === "minutes") {
      valueKey = "minutes";
    }

    if (data && data.length > 0) {
      // Extract values based on the determined key
      const values = data.map((item) => item[valueKey]);
      if (values.length > 0) {
        min = Math.max(0, Math.floor(Math.min(...values) * 0.9));
        max = Math.ceil(Math.max(...values) * 1.1);

        // Ensure we have a reasonable range
        if (max <= min) {
          max = min + 10;
        }
        // Ensure y-axis has enough space for the unit text
        if (unit === "minutes" && max < 50) {
          max = 60; // Ensure minimum scale for minutes
        }
      }
    }

    return {
      chart: {
        type: "line",
        toolbar: {
          show: true,
          offsetX: 8,
          offsetY: -4,
          tools: {
            download: '<i class="one-icons-outlined"> menu </i>', // Only keep the download option
            selection: false,
            zoom: false,
            zoomin: false,
            zoomout: false,
            pan: false,
            reset: false,
          },
          export: {
            csv: {
              filename: `patient-metrics-${title}`,
              columnDelimiter: ";",
              headerCategory: "Date",
              headerValue: title,
            },
            svg: {
              filename: `patient-metrics-${title}`,
            },
            png: {
              filename: `patient-metrics-${title}`,
            },
          },
        },
        zoom: {
          enabled: false, // Disable zoom functionality
        },
        fontFamily: "var(--one-font-family, var(--owc-font-family))",
        background: "transparent",
        animations: {
          enabled: true,
          speed: 800,
          animateGradually: {
            enabled: true,
            delay: 150,
          },
          dynamicAnimation: {
            enabled: true,
            speed: 350,
          },
        },
        offsetX: 15, // More space on the left side for y-axis labels and unit
      },
      stroke: {
        curve: "smooth", // From LINE_CHART_OPTIONS
        width: 2,
        colors: [color],
        lineCap: "round", // From LINE_CHART_OPTIONS
      },
      colors: [color],
      dataLabels: {
        enabled: false, // Keep disabled for cleaner look
        // From LINE_CHART_OPTIONS - Uncomment to enable values on chart
        /*
        distributed: false,
        offsetY: -5,
        background: {
          enabled: true,
          borderRadius: 8,
          opacity: 0.9,
          padding: 5
        },
        style: {
          fontSize: "12px",
          fontWeight: "normal",
          colors: ["var(--one-color-blue-500)", "var(--one-color-blue-800)"]
        }
        */
      },
      markers: {
        size: 4, // From LINE_CHART_OPTIONS (smaller than before)
        shape: "circle",
        colors: [color],
        strokeWidth: 2, // Slightly thicker border
        strokeColors: ["white"], // White border for better visibility
        hover: {
          size: 6, // From LINE_CHART_OPTIONS (smaller hover state)
          sizeOffset: 2,
        },
        discrete: [],
      },
      grid: {
        show: true,
        borderColor: "var(--one-color-gray-200)",
        strokeDashArray: 4, // From LINE_CHART_OPTIONS (slightly bigger dash)
        row: {
          colors: ["transparent", "var(--one-color-gray-50)"], // Alternate row colors from LINE_CHART_OPTIONS
          opacity: 0.5,
        },
        xaxis: {
          lines: {
            show: true,
          },
        },
        yaxis: {
          lines: {
            show: false, // Hide horizontal grid lines
          },
        },
        padding: {
          top: -5,
          right: 15,
          bottom: -5,
          left: 30, // More left padding for y-axis unit label
        },
      },
      tooltip: {
        enabled: true,
        shared: true,
        intersect: false,
        followCursor: true,
        theme: "light",
        x: {
          formatter: function (val) {
            return formatChartDate(val);
          },
        },
        y: {
          formatter: function (val) {
            return `${val} ${getUnitDisplay(unit, false)}`;
          },
          title: {
            formatter: function (seriesName) {
              return seriesName + ":"; // From LINE_CHART_OPTIONS - Clean series name in tooltip
            },
          },
        },
        style: {
          fontFamily: "var(--one-font-family, var(--owc-font-family))",
          fontSize: "12px",
        },
        marker: {
          show: true,
          fillColors: [color],
        },
        fixed: {
          enabled: false,
          position: "topRight",
        },
      },
      xaxis: {
        type: "datetime",
        labels: {
          style: {
            fontFamily: "var(--one-font-family, var(--owc-font-family))",
            fontSize: "12px",
            colors: "var(--one-color-gray-800)",
          },
          formatter: function (value) {
            return formatChartDate(value);
          },
          rotate: 0,
          offsetY: 2,
          offsetX: 0,
        },
        axisBorder: {
          show: true,
          color: "var(--one-color-gray-200)",
        },
        axisTicks: {
          show: true,
          color: "var(--one-color-gray-200)",
        },
        tickAmount: 6, // From LINE_CHART_OPTIONS (control number of x-axis labels)
      },
      yaxis: {
        min: min,
        max: max,
        forceNiceScale: true,
        tickAmount: 5,
        axisTicks: {
          show: false,
        },
        axisBorder: {
          show: false,
        },
        title: {
          text: getUnitDisplay(unit, true),
          style: {
            color: "var(--one-color-gray-600)", // From LINE_CHART_OPTIONS (slightly darker for better readability)
            fontSize: "12px",
            fontFamily: "var(--one-font-family, var(--owc-font-family))",
            fontWeight: 400, // From LINE_CHART_OPTIONS (normal weight)
          },
          offsetX: -12, // Position further left to avoid overlap with y-axis values
          offsetY: 0,
          rotate: -90, // Vertical text (bottom to top)
        },
        labels: {
          show: true,
          align: "right",
          style: {
            fontFamily: "var(--one-font-family, var(--owc-font-family))",
            fontSize: "12px",
            colors: "var(--one-color-gray-800)",
          },
          formatter: (value: number) => {
            return formatYAxisValue(value); // Only show the value without unit
          },
          minWidth: 20,
          maxWidth: 40, // Slightly wider to accommodate larger numbers
          offsetX: -5, // Move away from axis line a bit
          offsetY: 0,
        },
        floating: false,
        logarithmic: false, // From LINE_CHART_OPTIONS (ensure we're using linear scale)
      },
      legend: {
        show: true,
        position: "top",
        horizontalAlign: "right",
        floating: false,
        fontSize: "12px",
        fontFamily: "var(--one-font-family, var(--owc-font-family))",
        markers: {
          size: 10, // From LINE_CHART_OPTIONS
          strokeWidth: 0,
          offsetX: 0,
          offsetY: 0,
        },
        itemMargin: {
          horizontal: 12, // From LINE_CHART_OPTIONS (more space between legend items)
          vertical: 5,
        },
        onItemClick: {
          toggleDataSeries: true, // From LINE_CHART_OPTIONS (toggle series visibility)
        },
        formatter: undefined, // Default formatter
      },
      responsive: [
        {
          breakpoint: 768,
          options: {
            chart: {
              height: 280,
            },
            legend: {
              position: "bottom",
              horizontalAlign: "center",
            },
            yaxis: {
              title: {
                offsetX: -8, // Less offset on smaller screens
              },
            },
          },
        },
      ],
    };
  };

  const convertToApexSeries = (data: any[], valueKey: string, name: string) => {
    return [
      {
        name: name,
        data: data.map((item) => {
          // Debug to spot any zeros or improper values
          if (valueKey === "minutes" && item[valueKey] <= 0) {
            console.warn("Found zero or negative activity minutes:", item);
          }

          return {
            x: new Date(item.timestamp).getTime(),
            y: item[valueKey],
          };
        }),
      },
    ];
  };

  return (
    <div
      className="owc-flex owc-flex-column"
      style={{
        width: "100%",
        height: "100%",
        maxWidth: "100%",
        overflowX: "hidden",
      }}
    >
      {/* Patient Banner */}
      <OwcHeaderRow
        ref={headerRowRef}
        variant="tertiary"
        style={{ marginBottom: "24px" }}
      >
        <div
          className="owc-flex owc-align-center"
          style={{ width: "100%", padding: "0 16px" }}
        >
          <OwcIconButton
            icon="arrow_leftwards"
            family="outlined"
            onClick={() => navigate("/patients")}
            style={{ marginRight: "16px" }}
            aria-label="Back to patient list"
          />

          {/* Patient name fragment */}
          <div className="patient-banner-fragment">
            <div style={{ display: "flex", flexDirection: "column" }}>
              <OwcTypography
                style={{ fontSize: "1.25rem", fontWeight: "bold", margin: "0" }}
              >
                {patientDetails.lastName}, {patientDetails.firstName}
              </OwcTypography>
              <div
                style={{
                  fontSize: "0.875rem",
                  color: "var(--owc-color-neutral-60)",
                }}
              >
                ID: {patientDetails.id}
              </div>
            </div>
          </div>

          {/* Patient DOB fragment */}
          <div
            className="patient-banner-fragment"
            style={{ marginLeft: "32px" }}
          >
            <div style={{ display: "flex", flexDirection: "column" }}>
              <OwcTypography
                style={{
                  fontSize: "0.75rem",
                  color: "var(--owc-color-neutral-60)",
                  margin: "0",
                }}
              >
                Date of Birth
              </OwcTypography>
              <OwcTypography style={{ fontSize: "0.875rem", margin: "0" }}>
                {formatDate(patientDetails.dateOfBirth)}
              </OwcTypography>
            </div>
          </div>

          {/* Sex fragment */}
          <div
            className="patient-banner-fragment"
            style={{ marginLeft: "32px" }}
          >
            <div style={{ display: "flex", flexDirection: "column" }}>
              <OwcTypography
                style={{
                  fontSize: "0.75rem",
                  color: "var(--owc-color-neutral-60)",
                  margin: "0",
                }}
              >
                Sex
              </OwcTypography>
              <OwcTypography
                style={{
                  fontSize: "0.875rem",
                  margin: "0",
                  textTransform: "capitalize",
                }}
              >
                {patientDetails.sex}
              </OwcTypography>
            </div>
          </div>

          {/* MRN fragment */}
          <div
            className="patient-banner-fragment"
            style={{ marginLeft: "32px" }}
          >
            <div style={{ display: "flex", flexDirection: "column" }}>
              <OwcTypography
                style={{
                  fontSize: "0.75rem",
                  color: "var(--owc-color-neutral-60)",
                  margin: "0",
                }}
              >
                MRN
              </OwcTypography>
              <OwcTypography style={{ fontSize: "0.875rem", margin: "0" }}>
                {patientDetails.mrn}
              </OwcTypography>
            </div>
          </div>

          {/* Pregnancy Week fragment - only shown if applicable */}
          {patientDetails.weeksOfPregnancy && (
            <div
              className="patient-banner-fragment"
              style={{ marginLeft: "32px" }}
            >
              <div style={{ display: "flex", flexDirection: "column" }}>
                <OwcTypography
                  style={{
                    fontSize: "0.75rem",
                    color: "var(--owc-color-neutral-60)",
                    margin: "0",
                  }}
                >
                  Pregnancy
                </OwcTypography>
                <OwcTypography
                  style={{
                    fontSize: "0.875rem",
                    margin: "0",
                    fontWeight: "bold",
                    color: "var(--owc-color-success)",
                  }}
                >
                  Week {patientDetails.weeksOfPregnancy}
                </OwcTypography>
              </div>
            </div>
          )}

          {/* Right-aligned menu/options button */}
          <div
            style={{ marginLeft: "auto" }}
            className="patient-menu-container"
          >
            <OwcIconButton
              icon="more_vertical"
              family="outlined"
              aria-label="Patient options"
              id="patient-options-button"
              onClick={() =>
                document
                  .getElementById("patient-options-menu")
                  ?.setAttribute("visible", "true")
              }
            />
            <OwcMenu
              id="patient-options-menu"
              anchor="patient-options-button"
              position="bottom-end"
              style={{ minWidth: "200px" }}
              onClickOutside={() =>
                document
                  .getElementById("patient-options-menu")
                  ?.removeAttribute("visible")
              }
            >
              <OwcListItem onClick={() => console.log("Edit patient")}>
                <OwcIcon
                  slot="prefix"
                  family="outlined"
                  name="edit"
                  style={{ fontSize: "1rem" }}
                ></OwcIcon>
                Edit Patient
              </OwcListItem>
            </OwcMenu>
          </div>
        </div>
      </OwcHeaderRow>

      {/* Main content starts directly after the banner */}
      <div
        className="owc-flex-grow-1 owc-flex owc-flex-column"
        style={{
          overflow: "auto",
          width: "100%",
          padding: "8px 0",
          minWidth: "800px", // Prevent squishing on narrow screens
        }}
      >
        {/* Controls for metrics display */}
        <div
          className="owc-flex owc-mb-2"
          style={{ justifyContent: "space-between", alignItems: "center" }}
        >
          <OwcTypography style={{ fontSize: "1.5rem", fontWeight: "bold" }}>
            Health Metrics
          </OwcTypography>

          <div className="owc-flex owc-gap-md">
            <div
              style={{
                display: "flex",
                border: "1px solid var(--owc-color-neutral-30)",
                borderRadius: "4px",
              }}
            >
              <OwcButton
                variant={daysToShow === 7 ? "primary" : "secondary"}
                size="medium"
                onClick={() => handleDaysChange(7)}
              >
                7 Days
              </OwcButton>
              <OwcButton
                variant={daysToShow === 14 ? "primary" : "secondary"}
                size="medium"
                onClick={() => handleDaysChange(14)}
              >
                14 Days
              </OwcButton>
              <OwcButton
                variant={daysToShow === 30 ? "primary" : "secondary"}
                size="medium"
                onClick={() => handleDaysChange(30)}
              >
                30 Days
              </OwcButton>
            </div>

            {/* Toggle group for view switching */}
            <OwcToggleButtonGroup
              exclusive
              value={displayMode}
              onValueChange={(ev) => {
                console.log("Toggle value changed:", ev.detail);
                // Convert to string array for type safety
                const newValue = ev.detail.map((val) => String(val));
                setDisplayMode(newValue);
              }}
            >
              <OwcToggleButton value="graph">Graph</OwcToggleButton>
              <OwcToggleButton value="table">Table</OwcToggleButton>
            </OwcToggleButtonGroup>
          </div>
        </div>

        {/* Metrics Display */}
        <div
          className="owc-grid"
          style={{
            display: "grid",
            gridTemplateColumns:
              "repeat(auto-fit, minmax(min(100%, 600px), 1fr))",
            gap: "24px",
            width: "100%",
            overflowX: "hidden",
          }}
        >
          {/* Blood Glucose */}
          <div>
            <OwcCard>
              <div style={{ padding: "16px" }}>
                <OwcTypography
                  style={{
                    fontSize: "1.25rem",
                    fontWeight: "bold",
                    marginBottom: "16px",
                  }}
                >
                  Blood Glucose
                </OwcTypography>

                {displayMode.includes("graph") ? (
                  <div style={{ height: 300 }} className="patient-chart">
                    {bloodGlucoseData.length > 0 ? (
                      <ReactApexChart
                        options={getApexChartOptions(
                          "Blood Glucose",
                          bloodGlucoseData[0]?.unit || "mg/dL",
                          "#0078d4",
                          bloodGlucoseData
                        )}
                        series={convertToApexSeries(
                          bloodGlucoseData,
                          "value",
                          "Blood Glucose"
                        )}
                        type="line"
                        height={300}
                        width="100%"
                      />
                    ) : (
                      <NoDataDisplay metricName="blood glucose" />
                    )}
                  </div>
                ) : (
                  <div
                    style={{
                      height: "300px",
                      display: "flex",
                      flexDirection: "column",
                    }}
                  >
                    <OwcTable
                      style={{
                        height: "100%",
                        width: "100%",
                        display: "flex",
                        flexDirection: "column",
                        overflow: "auto",
                        flex: 1,
                      }}
                      aria-label="Blood Glucose Readings"
                    >
                      <OwcTableHeader sticky>
                        <OwcTableHeaderCell
                          width="35%"
                          align="left"
                          resizable
                          sortable
                        >
                          Date
                        </OwcTableHeaderCell>
                        <OwcTableHeaderCell
                          width="25%"
                          align="left"
                          resizable
                          sortable
                        >
                          Time
                        </OwcTableHeaderCell>
                        <OwcTableHeaderCell
                          width="40%"
                          align="left"
                          resizable
                          sortable
                        >
                          Blood Glucose
                        </OwcTableHeaderCell>
                      </OwcTableHeader>
                      <OwcTableBody>
                        {bloodGlucoseData.length === 0 ? (
                          <OwcTableRow>
                            <OwcTableCell style={{ width: "100%" }}>
                              <div
                                className="owc-flex owc-justify-center owc-align-center"
                                style={{ height: "100px", width: "100%" }}
                              >
                                <OwcTypography style={{ fontSize: "1rem" }}>
                                  No data available for the selected time period
                                </OwcTypography>
                              </div>
                            </OwcTableCell>
                          </OwcTableRow>
                        ) : (
                          bloodGlucoseData.map((reading, index) => {
                            const date = new Date(reading.timestamp);
                            return (
                              <OwcTableRow key={index}>
                                <OwcTableCell>
                                  {date.toLocaleDateString()}
                                </OwcTableCell>
                                <OwcTableCell>
                                  {date.toLocaleTimeString([], {
                                    hour: "2-digit",
                                    minute: "2-digit",
                                  })}
                                </OwcTableCell>
                                <OwcTableCell>
                                  {reading.value} {reading.unit}
                                </OwcTableCell>
                              </OwcTableRow>
                            );
                          })
                        )}
                      </OwcTableBody>
                    </OwcTable>
                  </div>
                )}
              </div>
            </OwcCard>
          </div>

          {/* Weight */}
          <div>
            <OwcCard>
              <div style={{ padding: "16px" }}>
                <OwcTypography
                  style={{
                    fontSize: "1.25rem",
                    fontWeight: "bold",
                    marginBottom: "16px",
                  }}
                >
                  Weight
                </OwcTypography>

                {displayMode.includes("graph") ? (
                  <div style={{ height: 300 }} className="patient-chart">
                    {weightData.length > 0 ? (
                      <ReactApexChart
                        options={getApexChartOptions(
                          "Weight",
                          weightData[0]?.unit || "kg",
                          "#107C10", // success color
                          weightData
                        )}
                        series={convertToApexSeries(
                          weightData,
                          "value",
                          "Weight"
                        )}
                        type="line"
                        height={300}
                        width="100%"
                      />
                    ) : (
                      <NoDataDisplay metricName="weight" />
                    )}
                  </div>
                ) : (
                  <div
                    style={{
                      height: "300px",
                      display: "flex",
                      flexDirection: "column",
                    }}
                  >
                    <OwcTable
                      style={{
                        height: "100%",
                        width: "100%",
                        display: "flex",
                        flexDirection: "column",
                        overflow: "auto",
                        flex: 1,
                      }}
                      aria-label="Weight Readings"
                    >
                      <OwcTableHeader sticky>
                        <OwcTableHeaderCell
                          width="35%"
                          align="left"
                          resizable
                          sortable
                        >
                          Date
                        </OwcTableHeaderCell>
                        <OwcTableHeaderCell
                          width="25%"
                          align="left"
                          resizable
                          sortable
                        >
                          Time
                        </OwcTableHeaderCell>
                        <OwcTableHeaderCell
                          width="40%"
                          align="left"
                          resizable
                          sortable
                        >
                          Weight
                        </OwcTableHeaderCell>
                      </OwcTableHeader>
                      <OwcTableBody>
                        {weightData.length === 0 ? (
                          <OwcTableRow>
                            <OwcTableCell style={{ width: "100%" }}>
                              <div
                                className="owc-flex owc-justify-center owc-align-center"
                                style={{ height: "100px", width: "100%" }}
                              >
                                <OwcTypography style={{ fontSize: "1rem" }}>
                                  No data available for the selected time period
                                </OwcTypography>
                              </div>
                            </OwcTableCell>
                          </OwcTableRow>
                        ) : (
                          weightData.map((reading, index) => {
                            const date = new Date(reading.timestamp);
                            return (
                              <OwcTableRow key={index}>
                                <OwcTableCell>
                                  {date.toLocaleDateString()}
                                </OwcTableCell>
                                <OwcTableCell>
                                  {date.toLocaleTimeString([], {
                                    hour: "2-digit",
                                    minute: "2-digit",
                                  })}
                                </OwcTableCell>
                                <OwcTableCell>
                                  {reading.value} {reading.unit}
                                </OwcTableCell>
                              </OwcTableRow>
                            );
                          })
                        )}
                      </OwcTableBody>
                    </OwcTable>
                  </div>
                )}
              </div>
            </OwcCard>
          </div>

          {/* Calories Intake */}
          <div>
            <OwcCard>
              <div style={{ padding: "16px" }}>
                <OwcTypography
                  style={{
                    fontSize: "1.25rem",
                    fontWeight: "bold",
                    marginBottom: "16px",
                  }}
                >
                  Calories Intake
                </OwcTypography>

                {displayMode.includes("graph") ? (
                  <div style={{ height: 300 }} className="patient-chart">
                    {caloriesData.length > 0 ? (
                      <ReactApexChart
                        options={getApexChartOptions(
                          "Calories Intake",
                          caloriesData[0]?.unit || "kcal",
                          "#ffb900", // warning color
                          caloriesData
                        )}
                        series={convertToApexSeries(
                          caloriesData,
                          "value",
                          "Calories"
                        )}
                        type="line"
                        height={300}
                        width="100%"
                      />
                    ) : (
                      <NoDataDisplay metricName="calorie intake" />
                    )}
                  </div>
                ) : (
                  <div
                    style={{
                      height: "300px",
                      display: "flex",
                      flexDirection: "column",
                    }}
                  >
                    <OwcTable
                      style={{
                        height: "100%",
                        width: "100%",
                        display: "flex",
                        flexDirection: "column",
                        overflow: "auto",
                        flex: 1,
                      }}
                      aria-label="Calories Intake"
                    >
                      <OwcTableHeader sticky>
                        <OwcTableHeaderCell
                          width="35%"
                          align="left"
                          resizable
                          sortable
                        >
                          Date
                        </OwcTableHeaderCell>
                        <OwcTableHeaderCell
                          width="25%"
                          align="left"
                          resizable
                          sortable
                        >
                          Time
                        </OwcTableHeaderCell>
                        <OwcTableHeaderCell
                          width="40%"
                          align="left"
                          resizable
                          sortable
                        >
                          Calories
                        </OwcTableHeaderCell>
                      </OwcTableHeader>
                      <OwcTableBody>
                        {caloriesData.length === 0 ? (
                          <OwcTableRow>
                            <OwcTableCell style={{ width: "100%" }}>
                              <div
                                className="owc-flex owc-justify-center owc-align-center"
                                style={{ height: "100px", width: "100%" }}
                              >
                                <OwcTypography style={{ fontSize: "1rem" }}>
                                  No data available for the selected time period
                                </OwcTypography>
                              </div>
                            </OwcTableCell>
                          </OwcTableRow>
                        ) : (
                          caloriesData.map((reading, index) => {
                            const date = new Date(reading.timestamp);
                            return (
                              <OwcTableRow key={index}>
                                <OwcTableCell>
                                  {date.toLocaleDateString()}
                                </OwcTableCell>
                                <OwcTableCell>
                                  {date.toLocaleTimeString([], {
                                    hour: "2-digit",
                                    minute: "2-digit",
                                  })}
                                </OwcTableCell>
                                <OwcTableCell>
                                  {reading.value} {reading.unit || "kcal"}
                                </OwcTableCell>
                              </OwcTableRow>
                            );
                          })
                        )}
                      </OwcTableBody>
                    </OwcTable>
                  </div>
                )}
              </div>
            </OwcCard>
          </div>

          {/* Physical Activity */}
          <div>
            <OwcCard>
              <div style={{ padding: "16px" }}>
                <OwcTypography
                  style={{
                    fontSize: "1.25rem",
                    fontWeight: "bold",
                    marginBottom: "16px",
                  }}
                >
                  Physical Activity
                </OwcTypography>

                {displayMode.includes("graph") ? (
                  <div style={{ height: 300 }} className="patient-chart">
                    {activityData.length > 0 ? (
                      <ReactApexChart
                        options={getApexChartOptions(
                          "Physical Activity",
                          "minutes",
                          "#2b88d8", // info color
                          activityData
                        )}
                        series={convertToApexSeries(
                          activityData,
                          "minutes",
                          "Activity"
                        )}
                        type="line"
                        height={300}
                        width="100%"
                      />
                    ) : (
                      <NoDataDisplay metricName="physical activity" />
                    )}
                  </div>
                ) : (
                  <div
                    style={{
                      height: "300px",
                      display: "flex",
                      flexDirection: "column",
                    }}
                  >
                    <OwcTable
                      style={{
                        height: "100%",
                        width: "100%",
                        display: "flex",
                        flexDirection: "column",
                        overflow: "auto",
                        flex: 1,
                      }}
                      aria-label="Physical Activity"
                    >
                      <OwcTableHeader sticky>
                        <OwcTableHeaderCell
                          width="35%"
                          align="left"
                          resizable
                          sortable
                        >
                          Date
                        </OwcTableHeaderCell>
                        <OwcTableHeaderCell
                          width="25%"
                          align="left"
                          resizable
                          sortable
                        >
                          Time
                        </OwcTableHeaderCell>
                        <OwcTableHeaderCell
                          width="40%"
                          align="left"
                          resizable
                          sortable
                        >
                          Duration
                        </OwcTableHeaderCell>
                      </OwcTableHeader>
                      <OwcTableBody>
                        {activityData.length === 0 ? (
                          <OwcTableRow>
                            <OwcTableCell style={{ width: "100%" }}>
                              <div
                                className="owc-flex owc-justify-center owc-align-center"
                                style={{ height: "100px", width: "100%" }}
                              >
                                <OwcTypography style={{ fontSize: "1rem" }}>
                                  No data available for the selected time period
                                </OwcTypography>
                              </div>
                            </OwcTableCell>
                          </OwcTableRow>
                        ) : (
                          activityData.map((reading, index) => {
                            const date = new Date(reading.timestamp);
                            return (
                              <OwcTableRow key={index}>
                                <OwcTableCell>
                                  {date.toLocaleDateString()}
                                </OwcTableCell>
                                <OwcTableCell>
                                  {date.toLocaleTimeString([], {
                                    hour: "2-digit",
                                    minute: "2-digit",
                                  })}
                                </OwcTableCell>
                                <OwcTableCell>
                                  {reading.minutes} minutes
                                </OwcTableCell>
                              </OwcTableRow>
                            );
                          })
                        )}
                      </OwcTableBody>
                    </OwcTable>
                  </div>
                )}
              </div>
            </OwcCard>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PatientDetail;
